import { useEffect, useRef, useState } from "react"
import { useQuery, keepPreviousData } from "@tanstack/react-query"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { formatNumber, decimalToPercent } from "@/app/lib/format"
import { toCsv, downloadCsv } from "@/app/lib/csv"
import type { SortDir } from "@/app/hooks/useTableSort"
import type { InterestKind } from "@/app/pages/NewTransactionPage/types"
import type { Transaction } from "@/app/types/inventory"

export const PER_PAGE_OPTIONS = [50, 100, 200] as const
export type PerPage = (typeof PER_PAGE_OPTIONS)[number]
const DEFAULT_PER_PAGE: PerPage = 50

// Sort keys whose column only exists on the leasehold tab — a mineral row has
// none of these, so a switch away from leasehold must drop an active sort on
// one of them (ports the pre-server-pagination `switchKind` behaviour).
const LEASEHOLD_ONLY_SORT_KEYS = new Set(["nri", "orri_reserved", "royalty"])

const SEARCH_DEBOUNCE_MS = 300
// Safety bound on the export page-fetch loop — `meta.total_pages` normally
// terminates it well before this; this only guards against a malformed
// response looping forever.
const MAX_EXPORT_PAGES = 1000
const EXPORT_PAGE_SIZE = 1000

export interface TransactionsMeta {
  total_count: number
  page: number
  per_page: number
  total_pages: number
}

export interface TransactionsSummary {
  acq_acres: string
  div_acres: string
  net_acres: string
}

interface TransactionsResponse {
  data: Transaction[]
  meta: TransactionsMeta
  summary: TransactionsSummary
}

export interface FilterOptions {
  township_ranges: string[]
  sections: string[]
  types: string[]
  interest_types: string[]
  total_count: number
}

const EMPTY_META: TransactionsMeta = {
  total_count: 0,
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  total_pages: 1,
}
const EMPTY_SUMMARY: TransactionsSummary = { acq_acres: "0", div_acres: "0", net_acres: "0" }
const emptyOptions = (totalCount: number): FilterOptions => ({
  township_ranges: [],
  sections: [],
  types: [],
  interest_types: [],
  total_count: totalCount,
})

// Small local debounce — the query key (and therefore the network request)
// only updates `SEARCH_DEBOUNCE_MS` after the user stops typing, while the
// search box itself stays fully responsive off the raw value.
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

interface BuildParamsArgs {
  kind: InterestKind
  q: string
  sections: string[]
  townshipRanges: string[]
  types: string[]
  interestTypes: string[]
  hasOrri: string
  sortKey: string | null
  sortDir: SortDir
  includeDeleted: boolean
  page: number
  perPage: number
}

// Rails-style repeated `key[]=value` array params, shared by the main query
// and the all-pages export loop (which only overrides page/perPage).
function buildParams(args: BuildParamsArgs): URLSearchParams {
  const params = new URLSearchParams()
  params.set("interest_kind", args.kind)
  if (args.q) params.set("q", args.q)
  for (const s of args.sections) params.append("section[]", s)
  for (const tr of args.townshipRanges) params.append("township_range[]", tr)
  for (const t of args.types) params.append("type[]", t)
  for (const it of args.interestTypes) params.append("interest_type[]", it)
  if (args.hasOrri) params.set("has_orri", args.hasOrri)
  if (args.sortKey) {
    params.set("sort", args.sortKey)
    params.set("dir", args.sortDir)
  }
  if (args.includeDeleted) params.set("include_deleted", "1")
  params.set("page", String(args.page))
  params.set("per_page", String(args.perPage))
  return params
}

// Client-side CSV of the current view (filters + sort, deleted rows included
// when shown). Values mirror the on-screen cells — ported unchanged from the
// pre-pagination TransactionsTable.handleExport.
function buildCsv(kind: InterestKind, rows: Transaction[]): string {
  const headers =
    kind === "leasehold"
      ? [
          "TX #",
          "Date",
          "Type",
          "STR",
          "T-R",
          "Int. Status",
          "Net Acres",
          "NRI",
          "ORRI Res",
          "Counterparty",
          "Book/Page",
          "Notes",
        ]
      : [
          "TX #",
          "Date",
          "Type",
          "STR",
          "T-R",
          "Int. Status",
          "Net Acres",
          "Royalty",
          "Legal Desc",
          "Counterparty",
          "Book/Page",
          "Notes",
        ]
  const csvRows = rows.map((r) => {
    const typeLabel = r.type === "ACQ" ? "Acquisition" : r.type === "DIV" ? "Divestiture" : r.type
    const shared = [
      r.tx_number,
      r.transaction_date,
      typeLabel,
      r.str,
      r.township_range,
      r.interest_type ?? "",
      formatNumber(r.net_acres, 4),
    ]
    const tail = [r.counterparty ?? "", r.book_page ?? "", r.notes ?? ""]
    if (kind === "leasehold") {
      const orriNum = Number(r.orri_reserved)
      return [
        ...shared,
        formatNumber(r.nri, 4),
        orriNum > 0 ? decimalToPercent(orriNum, 2) : "",
        ...tail,
      ]
    }
    return [
      ...shared,
      r.royalty == null ? "" : formatNumber(r.royalty, 4),
      r.legal_desc ?? "",
      ...tail,
    ]
  })
  return toCsv(headers, csvRows)
}

export interface UseTransactionsViewArgs {
  /** ?tx= deep-link target, if any. */
  initialSearch?: string
  includeDeleted: boolean
}

/**
 * Owns all Transactions-page view state (tab, filters, sort, pagination) and
 * the two server queries that back it — the paginated rows + full-scope KPI
 * summary, and the per-kind filter option lists. See
 * .claude/Plans/2026-08-04-transactions-pagination-kpi.md for the API contract.
 */
export function useTransactionsView({ initialSearch, includeDeleted }: UseTransactionsViewArgs) {
  const { theme } = useClientTheme()
  const { user } = useCurrentUser()

  // A ?tx= deep-link must land on the tab containing its target — the search
  // filter can't cross tabs. Resolve the target's kind via a one-shot lookup
  // (no interest_kind param) before the kind-scoped main query is allowed to
  // run at all; today's in-memory grep is impossible once rows are paginated.
  const [kind, setKindState] = useState<InterestKind>("leasehold")
  const lookupQuery = useQuery<TransactionsResponse>({
    queryKey: ["transactions", "lookup", initialSearch],
    queryFn: () =>
      api.get<TransactionsResponse>(
        `/transactions?q=${encodeURIComponent(initialSearch!)}&per_page=1`
      ),
    enabled: !!initialSearch,
    staleTime: Infinity,
  })
  useEffect(() => {
    if (!initialSearch || !lookupQuery.data) return
    setKindState(lookupQuery.data.data[0]?.interest_kind ?? "leasehold")
  }, [initialSearch, lookupQuery.data])
  const deepLinkReady = !initialSearch || lookupQuery.isSuccess || lookupQuery.isError

  const [page, setPageState] = useState(1)
  const [perPage, setPerPageState] = useState<PerPage>(DEFAULT_PER_PAGE)
  const [q, setQState] = useState(initialSearch ?? "")
  const debouncedQ = useDebouncedValue(q, SEARCH_DEBOUNCE_MS)
  const [sectionSel, setSectionSelState] = useState<string[]>([])
  const [trSel, setTrSelState] = useState<string[]>([])
  const [typeSel, setTypeSelState] = useState<string[]>([])
  const [interestSel, setInterestSelState] = useState<string[]>([])
  // "" = All, "yes" = ORRI Res > 0, "no" = no ORRI Res.
  const [hasOrriSel, setHasOrriSelState] = useState("")
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>("asc")

  const resetPage = () => setPageState(1)

  const setQ = (v: string) => {
    setQState(v)
    resetPage()
  }
  const setSectionSel = (v: string[]) => {
    setSectionSelState(v)
    resetPage()
  }
  const setTrSel = (v: string[]) => {
    setTrSelState(v)
    resetPage()
  }
  const setTypeSel = (v: string[]) => {
    setTypeSelState(v)
    resetPage()
  }
  const setInterestSel = (v: string[]) => {
    setInterestSelState(v)
    resetPage()
  }
  const setHasOrriSel = (v: string) => {
    setHasOrriSelState(v)
    resetPage()
  }
  const setPerPage = (v: PerPage) => {
    setPerPageState(v)
    resetPage()
  }
  const setPage = (p: number) => setPageState(p)

  // Kind switch: ORRI-has filter only exists on leasehold — a hidden active
  // value would filter mineral rows invisibly, so clear it. Drop a sort keyed
  // on a column the other tab doesn't have. Other filters carry across tabs.
  const setKind = (next: InterestKind) => {
    if (next === kind) return
    setKindState(next)
    setHasOrriSelState("")
    if (sortKey && LEASEHOLD_ONLY_SORT_KEYS.has(sortKey)) {
      setSortKey(null)
      setSortDir("asc")
    }
    resetPage()
  }

  // Mirrors useTableSort's toggle semantics: same column flips direction, a
  // new column starts ascending. useTableSort itself is untouched — other
  // tables still sort client-side with it. A sort change jumps back to page 1
  // — the point of re-sorting is to see the top of the new order.
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("asc")
    }
    resetPage()
  }

  const hasFilters =
    !!q ||
    trSel.length > 0 ||
    typeSel.length > 0 ||
    interestSel.length > 0 ||
    sectionSel.length > 0 ||
    hasOrriSel !== ""
  const clearFilters = () => {
    setQState("")
    setTrSelState([])
    setTypeSelState([])
    setInterestSelState([])
    setSectionSelState([])
    setHasOrriSelState("")
    resetPage()
  }

  // `includeDeleted` is owned by the page (the "Show deleted" checkbox), not
  // by this hook — but toggling it changes the scope just like a filter, so
  // it needs the same page-1 reset (skipped on mount).
  const isFirstIncludeDeletedRender = useRef(true)
  useEffect(() => {
    if (isFirstIncludeDeletedRender.current) {
      isFirstIncludeDeletedRender.current = false
      return
    }
    resetPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted])

  const mainQuery = useQuery<TransactionsResponse>({
    queryKey: [
      "transactions",
      {
        kind,
        page,
        perPage,
        q: debouncedQ,
        sectionSel,
        trSel,
        typeSel,
        interestSel,
        hasOrriSel,
        sortKey,
        sortDir,
        includeDeleted,
      },
    ],
    queryFn: () => {
      const params = buildParams({
        kind,
        q: debouncedQ,
        sections: sectionSel,
        townshipRanges: trSel,
        types: typeSel,
        interestTypes: interestSel,
        hasOrri: hasOrriSel,
        sortKey,
        sortDir,
        includeDeleted,
        page,
        perPage,
      })
      return api.get<TransactionsResponse>(`/transactions?${params.toString()}`)
    },
    enabled: deepLinkReady,
    placeholderData: keepPreviousData,
  })

  const optionsQuery = useQuery<FilterOptions>({
    queryKey: ["transactions", "filter_options", kind, includeDeleted],
    queryFn: () => {
      const params = new URLSearchParams({ interest_kind: kind })
      if (includeDeleted) params.set("include_deleted", "1")
      return api.get<FilterOptions>(`/transactions/filter_options?${params.toString()}`)
    },
    enabled: deepLinkReady,
    placeholderData: keepPreviousData,
  })

  const rows = mainQuery.data?.data ?? []
  const meta = mainQuery.data?.meta ?? EMPTY_META
  const summary = mainQuery.data?.summary ?? EMPTY_SUMMARY
  const options = optionsQuery.data ?? emptyOptions(meta.total_count)
  const isLoading = mainQuery.isPending

  const [isExporting, setIsExporting] = useState(false)
  const exportCsv = async () => {
    setIsExporting(true)
    try {
      const allRows: Transaction[] = []
      let fetchedPage = 1
      let totalPages = 1
      do {
        const params = buildParams({
          kind,
          q: debouncedQ,
          sections: sectionSel,
          townshipRanges: trSel,
          types: typeSel,
          interestTypes: interestSel,
          hasOrri: hasOrriSel,
          sortKey,
          sortDir,
          includeDeleted,
          page: fetchedPage,
          perPage: EXPORT_PAGE_SIZE,
        })
        const res = await api.get<TransactionsResponse>(`/transactions?${params.toString()}`)
        allRows.push(...res.data)
        totalPages = res.meta.total_pages
        fetchedPage += 1
      } while (fetchedPage <= totalPages && fetchedPage <= MAX_EXPORT_PAGES)

      // "Cherokee Moon Transactions - 7-31-2026.csv"
      const clientName = user?.client?.name || theme.clientName
      const date = new Date().toLocaleDateString("en-US").replace(/\//g, "-")
      downloadCsv(`${clientName} Transactions - ${date}.csv`, buildCsv(kind, allRows))
    } finally {
      setIsExporting(false)
    }
  }

  return {
    kind,
    setKind,
    q,
    setQ,
    sectionSel,
    setSectionSel,
    trSel,
    setTrSel,
    typeSel,
    setTypeSel,
    interestSel,
    setInterestSel,
    hasOrriSel,
    setHasOrriSel,
    hasFilters,
    clearFilters,
    sortKey,
    sortDir,
    handleSort,
    page,
    setPage,
    perPage,
    setPerPage,
    rows,
    meta,
    summary,
    options,
    isLoading,
    isFetching: mainQuery.isFetching,
    exportCsv,
    isExporting,
  }
}
